/**
 * Working Groups Data Transformation Utility - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * A specialized utility for transforming working groups data with advanced features:
 * - Flat data structure to hierarchical transformation
 * - Working group type and category organization
 * - Color scheme mapping for visual hierarchy
 * - Steering group relationship processing
 * - Working group ordering and categorization
 * - Visual hierarchy structure generation
 * - Type-based color assignment
 * - Organizational structure optimization
 * 
 * Features:
 * - Transforms flat working group data into structured hierarchy
 * - Maps working group types to visual categories
 * - Applies consistent color schemes across groups
 * - Handles steering group relationships and assignments
 * - Sorts groups by order and priority
 * - Generates structured output for visual components
 * - Supports multiple working group types
 * 
 * Working Group Types:
 * - Working Groups: Core operational groups
 * - Project Groups: Task-specific project teams
 * - Specialist Groups: Expert knowledge groups
 * - Industry Fora: External engagement groups
 * - Steering Groups: Leadership and oversight groups
 * 
 * Transformation Process:
 * 1. Parse group types and apply ordering
 * 2. Extract steering groups with color mapping
 * 3. Process working groups by type and category
 * 4. Apply color schemes based on steering group relationships
 * 5. Generate structured hierarchy for UI components
 * 
 * Output Structure:
 * - wgGroups: Group type categories with colors and ordering
 * - wgSteeringGroups: Leadership groups with visual styling
 * - wgSubGroups: Individual working groups with relationships
 * 
 * @param {Array} flatData - Flat array of working group data
 * @param {Array} groupTypes - Array of working group type definitions
 * @returns {Object} Structured working groups hierarchy for UI rendering
 */
export function transformWorkingGroups(flatData, groupTypes) {
  
    // Define color mappings for steering groups (this could also come from a file if needed)
    const steeringGroupColors = {
      'Capability & Innovation': { BackColor: '#843390', Color: '#FFFFFF' },
      'Advocacy': { BackColor: '#006d92', Color: '#FFFFFF' },
      'Talent': { BackColor: '#eb7b15', Color: '#FFFFFF' },
      'Engagement & Promotion': { BackColor: '#56b32f', Color: '#FFFFFF' },
      'Tax': { BackColor: '#1614fd', Color: '#FFFFFF' }
    };
  
    const structure = {
      // Map the group types directly from the new file
      wgGroups: groupTypes.sort((a, b) => a.order - b.order).map(type => ({
        ID: type.id,
        Title: type.title,
        BackColor: type.backColor,
        Color: type.color,
        GroupType: type.groupType,
        ...(type.arrowLeft && { ArrowLeft: "true" })
      })),
      wgSteeringGroups: [],
      wgSubGroups: []
    };


  
    // Process steering groups first
    const steeringGroups = flatData.filter(item => item.workingGroupType === "Steering Group");
    structure.wgSteeringGroups = steeringGroups.map((group) => ({
      ID: group.id,
      Title: group.workingGroupName,
      BackColor: steeringGroupColors[group.workingGroupName]?.BackColor || '#843390',
      Color: "#FFFFFF"
    }));
  
    // Process all other groups
    const workingGroupMap = {
      'Working Group': 'Working Groups',
      'Project Group': 'Project Groups',
      'Specialist Group': 'Specialist Groups',
      'Industry Fora': 'Industry Fora'
    };

    // Define default colors for each group type
    const defaultGroupColors = {
      'Working Group': 'var(--ifia-light-blue)',
      'Project Group': 'var(--ifia-purple)',  
      'Specialist Group': '#69e5b3',
      'Industry Fora': 'var(--ifia-fora-blue)'    
    };
  
    // Rest of the transformation remains similar, but use groupTypes for default colors
    const getDefaultColor = (workingGroupType) => {
      return defaultGroupColors[workingGroupType] || 'var(--ifia-blue)';
    };
  
    flatData.forEach(item => {
      if (item.workingGroupType === "Steering Group") return;
  
      const backColor = item.steeringGroupName && item.steeringGroupName !== "N/a" ? 
        steeringGroupColors[item.steeringGroupName]?.BackColor : 
        getDefaultColor(item.workingGroupType);
  
      structure.wgSubGroups.push({
        ID: item.id,
        Title: item.workingGroupName,
        WorkingGroup: workingGroupMap[item.workingGroupType],
        WorkingGroupId: workingGroupMap[item.workingGroupType] === 'Working Groups' ? "1" :
                       workingGroupMap[item.workingGroupType] === 'Project Groups' ? "3" :
                       workingGroupMap[item.workingGroupType] === 'Specialist Groups' ? "4" : "5",
        SteeringGroup: item.steeringGroupName === "N/a" ? "" : item.steeringGroupName,
        SteeringGroupID: item.steeringGroupName && item.steeringGroupName !== "N/a" ? 
          flatData.find(sg => sg.workingGroupType === "Steering Group" && sg.workingGroupName === item.steeringGroupName)?.id.toString() || "" : 
          "",
        BackColor: backColor,
        Color: "#FFFFFF"
      });
    });
  
    return structure;
  }